import _ from 'lodash';
import React, { useEffect } from 'react';
import OtxNumber from '~/src/components/shared/OtxNumber';
import PhotoVideo from '~/src/components/shared/PhotoVideo';
import Urls from '~/src/constants/urls';
import useFetch from '~/src/hooks/useFetch';
import {
	imgIcon18UpSvg,
	imgIconDrinkSvg,
	imgIconHeadphonesSvg,
	imgIconStreamingSvg,
	imgPlaceholdersKeyArtWebp,
} from '~/src/images';
import {
	GetDateTimeString,
	HasAllFightResults,
	IsInPast,
	IsInProgress,
	IsUpcoming,
	OtxEvent,
} from '~/src/models/OtxEvent';
import { FightTypes } from '~/src/models/OtxFight';
import { EventInfoResponse, SaleStatuses } from '~/src/models/Vivenu';
import './Banner.scss';

const getVivenuEventIsSoldOut = (event: OtxEvent) => {
	const vivenuEvent = useFetch<EventInfoResponse>({
		url: `${Urls.ApiPlatformsVivenuEventInfo}/${event.vivenu_id}`,
		key: 'event',
	})?.result.read();

	return vivenuEvent?.saleStatus === SaleStatuses.SoldOut;
};

const Banner = ({ event }: { event: OtxEvent }) => {
	const [fightDate, setFightDate] = React.useState<string>(
		GetDateTimeString(new Date(event.starts_at), 'America/New_York'),
	);

	useEffect(() => {
		setFightDate(GetDateTimeString(new Date(event.starts_at), event.starts_at_timezone));
	}, [event.starts_at, event.starts_at_timezone]);

	const isSoldOut = event.is_sold_out || getVivenuEventIsSoldOut(event);

	const mainEventFight = event?.otx_fights?.find((f) => f.fight_type === FightTypes.MainEvent);
	const isOtxEvent = !!event.otx_number;

	const nextFight = _.orderBy(
		event?.otx_fights,
		[
			(f) => f.fight_type === FightTypes.MainEvent,
			(f) => f.fight_type === FightTypes.CoMainEvent,
			(f) => f.fight_type === FightTypes.Featured,
		],
		['desc', 'desc', 'desc'],
	).at(0);

	const fighter1 = nextFight?.otx_fighter1;
	const fighter2 = nextFight?.otx_fighter2;

	const isFightTbd = !fighter1 || !fighter2 || (isOtxEvent && !mainEventFight);

	return (
		<div className="events-banner">
			<div className="hero-image">
				{event.image_path ? (
					<PhotoVideo src={`${Urls.OvertimeImagesOtx}/${event.image_path}`} mobileWidth={750} />
				) : (
					<img src={imgPlaceholdersKeyArtWebp} />
				)}
			</div>
			<div className="info">
				<OtxNumber otxNumber={event.otx_number} />
				<div className="fighters">
					{event.is_custom_title ? (
						<>{event.title}</>
					) : isFightTbd ? (
						<>To be announced</>
					) : (
						<>
							{fighter1.last_name} vs {fighter2.last_name}
						</>
					)}
				</div>
				<div className="date">{fightDate}</div>
				<div className="highlights">
					<>
						<div className="highlight-icon">
							<img src={imgIconStreamingSvg} />
						</div>
						<div className="highlight-text">
							{/* If Nicolson vs Brown fight, then only show DAZN */}
							{/* TODO: Add admin logic to control which stream platforms appear here */}
							{event.id == '2ebe0296-1a1b-4601-8eba-c22aa4180010' ? 'DAZN' : 'DAZN, YouTube, TikTok, Instagram'}
						</div>
					</>
					{event.dj_name && (
						<>
							<div className="highlight-icon">
								<img src={imgIconHeadphonesSvg} />
							</div>
							<div className="highlight-text highlight-text--uppercase">{event.dj_name}</div>
						</>
					)}
					{event.is_18_plus ? (
						<>
							<div className="highlight-icon">
								<img src={imgIcon18UpSvg} />
							</div>
							<div className="highlight-text">18+ Only</div>
						</>
					) : null}
					{event.is_alcohol_served ? (
						<>
							<div className="highlight-icon">
								<img src={imgIconDrinkSvg} />
							</div>
							<div className="highlight-text">Alcohol served</div>
						</>
					) : null}
				</div>

				<div className="buttons">
					{event.cta_url && event.cta_text && IsUpcoming(event) ? (
						<a className="cta primary" href={event.cta_url}>
							{event.cta_text}
						</a>
					) : event.vivenu_id && !isSoldOut && IsUpcoming(event) ? (
						<a className="cta primary" href={event.vivenu_id ? `${Urls.VivenuEventBase}/${event.vivenu_id}` : null}>
							Buy Tickets
						</a>
					) : event.vivenu_id && isSoldOut && IsUpcoming(event) ? (
						<a className="cta" style={{ cursor: 'default' }}>
							Sold Out
						</a>
					) : (
						<></>
					)}

					{event.dazn_url && IsInProgress(event) ? (
						<a className="cta primary" href={event.dazn_url}>
							Watch Live
						</a>
					) : event.dazn_url && IsUpcoming(event) ? (
						<a className="cta secondary" href={event.dazn_url}>
							Watch Live
						</a>
					) : event.dazn_url && (HasAllFightResults(event) || IsInPast(event)) ? (
						<a className="cta secondary" href={event.dazn_url}>
							Replay
						</a>
					) : null}
				</div>
			</div>
		</div>
	);
};

// Note: There can only be one export per page component for hot loading to work
export default Banner;
